/**
 * Painel Berytus › Curadoria: divergências da PharmaDB. Receita retida/controle especial (a), tarja (b) e controlado ×
 * lista da Portaria 344 (c), comparando a PharmaDB com a ficha conferida do mestre. Só sinaliza: a PharmaDB nunca muda
 * esses campos no mestre (precedência: CMED/ABCFarma e a lista da 344), nem o anúncio, nem a regra block_controlled.
 * A correção é manual na ficha (Catálogo mestre); aqui fica a decisão, no audit_log. Contagem por tipo com a Qualidoc
 * em coluna própria.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';

interface Div { id: number; type: 'a' | 'b' | 'c'; label: string; catalogProductId: string; title: string; gtin: string | null; master: Record<string, unknown>; pharmadb: Record<string, unknown>;
  qualidoc: boolean; status: string; createdAt: string; decidedAt: string | null; decidedBy: string | null }
interface Report { type: string; label: string; qualidoc: number; others: number }

const show = (v: Record<string, unknown>) => Object.entries(v ?? {}).filter(([, x]) => x != null && x !== '')
  .map(([k, x]) => `${k}: ${typeof x === 'boolean' ? (x ? 'sim' : 'não') : String(x)}`).join(' · ') || '—';

export function AdminDivergences() {
  const [type, setType] = useState<'' | 'a' | 'b' | 'c'>('');
  const [status, setStatus] = useState<'pending' | 'decided'>('pending');
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'divergences', type, status], queryFn: () => api<{ report: Report[]; items: Div[] }>(`/admin/curation/divergences?status=${status}${type ? `&type=${type}` : ''}`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'divergences'] });
  const recompute = useMutation({ mutationFn: () => api('/admin/curation/divergences/recompute', { method: 'POST', json: {} }), onSuccess: refresh });
  const decide = useMutation({ mutationFn: (v: { id: number; decision: 'corrigido' | 'mestre_correto'; note: string }) => api(`/admin/curation/divergences/${v.id}/decide`, { method: 'POST', json: { decision: v.decision, note: v.note } }), onSuccess: refresh });
  const ask = (id: number, decision: 'corrigido' | 'mestre_correto') => {
    const note = window.prompt(decision === 'corrigido' ? 'O que foi corrigido na ficha (Catálogo mestre)?' : 'Por que o mestre está correto?');
    if (note && note.trim().length >= 3) decide.mutate({ id, decision, note: note.trim() });
  };
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Curadoria: divergências da PharmaDB</h1>
          <p>Receita, tarja e controlado da PharmaDB × ficha conferida. Só sinaliza: o mestre continua decidindo (CMED/ABCFarma e Portaria 344). Corrija na ficha e registre a decisão aqui.</p>
        </div>
        <button type="button" className="btn" disabled={recompute.isPending} onClick={() => recompute.mutate()}>{recompute.isPending ? 'Recalculando…' : 'Recalcular'}</button>
      </div>
      {q.data && (
        <section className="panel flush" style={{ marginBottom: 12 }}>
          <table className="simple">
            <thead><tr><th>Tipo</th><th className="num">Qualidoc</th><th className="num">Outras contas / sem conta</th></tr></thead>
            <tbody>{q.data.report.map((r) => <tr key={r.type}><td><b>{r.type})</b> {r.label}</td><td className="num">{int(r.qualidoc)}</td><td className="num">{int(r.others)}</td></tr>)}</tbody>
          </table>
        </section>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div className="seg" role="group" aria-label="Tipo">
          {([['', 'Todas'], ['a', 'a) Receita retida'], ['b', 'b) Tarja'], ['c', 'c) Controlado']] as const).map(([v, l]) => <button key={v} type="button" aria-pressed={type === v} onClick={() => setType(v)}>{l}</button>)}
        </div>
        <div className="seg" role="group" aria-label="Situação">
          {([['pending', 'Pendentes'], ['decided', 'Decididas']] as const).map(([v, l]) => <button key={v} type="button" aria-pressed={status === v} onClick={() => setStatus(v)}>{l}</button>)}
        </div>
      </div>
      {(decide.error || recompute.error) && <ErrorBox error={decide.error ?? recompute.error} />}
      {q.error ? <ErrorBox error={q.error} /> : !q.data ? <Loading /> : !q.data.items.length ? <div className="empty"><b>Nada aqui</b>Nenhuma divergência nesta situação.</div> : (
        <section className="panel flush">
          <div className="grid-wrap">
            <table className="grid" style={{ width: '100%', minWidth: 900 }}>
              <thead><tr><th>Ficha</th><th>Tipo</th><th>Mestre</th><th>PharmaDB</th><th>Situação</th><th /></tr></thead>
              <tbody>{q.data.items.map((d) => (
                <tr key={d.id} style={{ cursor: 'default' }}>
                  <td style={{ whiteSpace: 'normal', maxWidth: 280 }}><Link to={`/admin/catalogo/${d.catalogProductId}`}><b>{d.title}</b></Link>
                    <div className="small muted mono">{d.gtin ?? '—'}</div>{d.qualidoc && <Tag tone="info">Qualidoc</Tag>}</td>
                  <td>{d.type}) {d.label}</td>
                  <td className="small" style={{ whiteSpace: 'normal', maxWidth: 220 }}>{show(d.master)}</td>
                  <td className="small" style={{ whiteSpace: 'normal', maxWidth: 220 }}>{show(d.pharmadb)}</td>
                  <td className="small">{d.status === 'pending' ? <Tag tone="warn">pendente</Tag> : <Tag tone={d.status === 'accepted' ? 'ok' : 'off'}>{d.status === 'accepted' ? 'corrigido' : 'mestre correto'}</Tag>}
                    <div className="muted">{dateTime(d.decidedAt ?? d.createdAt)}{d.decidedBy ? ` · ${d.decidedBy}` : ''}</div></td>
                  <td>{d.status === 'pending' && (
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button type="button" className="btn sm" disabled={decide.isPending} onClick={() => ask(d.id, 'corrigido')}>Corrigi a ficha</button>
                      <button type="button" className="btn sm quiet" disabled={decide.isPending} onClick={() => ask(d.id, 'mestre_correto')}>Mestre correto</button>
                    </div>
                  )}</td>
                </tr>))}</tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
